import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import * as router from '@attain-apps/fe/web/main/lib/router'

import * as state from '../../../../../../../../../state'
import * as componentStore from '../../component-store'
import { isLayoutValid, optimizeLayout } from '../../optimize-layout'

@ngCore.Component({
  imports: [ds.overlay.attached.Component],
  selector: 'atn-page-builder-overflow-actions',
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link componentStore.builder.Store} */
  public readonly builderStore = ngCore.inject(componentStore.builder.Store)

  /** @see {@link componentStore.configuration.Store} */
  private readonly _configurationStore = ngCore.inject(
    componentStore.configuration.Store,
  )

  /**
   * The element that the overlay is connected to.
   */
  public originSg = ngCore.input.required<HTMLElement>({ alias: 'origin' })

  /** @see {@link state.mosaic.dev.pages.Collection} */
  private readonly _pagesEc = ngCore.inject(state.mosaic.dev.pages.Collection)

  /** @see {@link state.me.Feature} */
  public readonly meFeature = ngCore.inject(state.me.Feature)

  /** @see {@link router.Router} */
  private readonly _router = ngCore.inject(router.Router)

  /**
   * Marks the current state for discard and opens the discard changes dialog
   */
  public cancelButtonClickHandler() {
    this.builderStore.patchState({
      markedForDiscard: true,
    })
  }

  /**
   * Handles the save button click by saving and processing the current segment layout.
   * Converts the layout into {@link ents.core.mosaic.Page.Content} format.
   */
  public saveButtonClickHandler() {
    this.builderStore.saveCurrentSegmentLayout()
    this.optimizeLayout()

    const { segmentLookup, widgetFrameLookup, grid } = this.builderStore.state()
    if (!grid) {
      return
    }
    const pageContent = state.mosaic.dev.pages.models.Segment.Lookup.toContent(
      segmentLookup,
      grid.getCellHeight(),
      widgetFrameLookup,
    )

    const source = 'PageBuilder.Save.Button.Clicked'
    // TODO: Consider updating page content on other place, not in function for
    // saving page
    // Updating page content
    this._pagesEc.plugins.editingPlugin.feature.actions.edit
      .from(source)
      .dispatch({
        edit: page => {
          if (!page) {
            return
          }
          page.content = pageContent
        },
      })

    // Submit page changes
    this._pagesEc.plugins.editingPlugin.feature.actions.submit
      .from(source)
      .dispatch({
        current: true,
      })

    const url = this.meFeature.contexts.selectors
      .getUrlForLink({
        goto: {
          navigationId: ents.extra.prebuilt.AppPageAlias.Pages,
        },
      })
      .asSignal()

    if (url) {
      this._router.actions.navigate.from(source).dispatch({ url })
    }
  }

  /**
   * Creates new segment if layout is valid. Otherwise prompts for layout
   * optimization confirmation for the current segment before creating a new
   * one.
   */
  public addSegmentClickHandler(): void {
    const grid = this.builderStore.gridSg()
    if (!grid) {
      return
    }

    if (!isLayoutValid(grid, this.builderStore.widgetFrameLookupSg())) {
      this.builderStore.patchState({
        switchingSegmentConfiguration: {
          isNew: true,
        },
      })
    } else {
      this.builderStore.createSegment()
    }
  }

  public addWidgetClickHandler(): void {
    this.builderStore.addWidgetFrame()
  }

  /**
   * Shows the page configuration panel and hides overflow actions
   * to reduce UI clutter during configuration mode.
   */
  public configurePageClickHandler() {
    this.builderStore.patchState({
      presentOverflowActions: false,
    })

    this._configurationStore.patchState({ presentPageConfiguration: true })
  }

  /**
   * TODO
   */
  public backdropClickHandler() {
    this.builderStore.patchState({
      presentOverflowActions: false,
    })
  }

  /**
   * @see {@link optimizeLayout}
   */
  public optimizeLayout(): void {
    const grid = this.builderStore.gridSg()
    if (!grid) {
      return
    }

    const widgetLookup = this.builderStore.widgetFrameLookupSg()

    if (!isLayoutValid(grid, widgetLookup)) {
      optimizeLayout(grid, widgetLookup)
      // save changes to current segment layout
      this.builderStore.saveCurrentSegmentLayout()
    }
  }
}
